import moment from 'moment';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { IPortfolioPerformanceAccounts } from '../portfolio-performance.types';
import { useAccountTicker } from './use-account-ticker';
import { useMetricTimeseries } from './use-metric-timeseries';
import { MetricKey, METRICS, TickPayload } from './risk.types';
import { useSmartInactivityWake } from '../../../hooks/use-smart-inactivity-wake';
import {
  frequencySecondsFor,
  windowSecondsFor,
  COMBINED_USD_TOKEN,
  BTC_USD_SYMBOL,
} from './risk.constants';
import {
  contributingSymbolsForMetric,
  getCombinedUsdSymbols,
  inferDefaultRiskTokenType,
} from './risk.helpers';

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10min

type MetricState = ReturnType<typeof useMetricTimeseries>;

export const useAccountPerformance = (account: IPortfolioPerformanceAccounts) => {
  const symbolBTCUSD = account.symbols.find((s) => s.toUpperCase() === BTC_USD_SYMBOL);

  // Constituent settlement books to fold into the combined-USD view (empty for
  // BASE accounts or any account with nothing to combine). When present, the
  // combined total is the correct headline for a USD-denominated account, so it
  // is the default selection; the individual books stay selectable.
  const combinedSymbols = useMemo(() => getCombinedUsdSymbols(account), [account]);

  const [selectedToken, setSelectedToken] = useState<string>(
    combinedSymbols.length ? COMBINED_USD_TOKEN : symbolBTCUSD || account.symbols[0] || '',
  );
  // 3M as the default pulled in ~1080 points per metric and felt slow to
  // load even though the RPC itself is fast (sub-second) — 1W keeps the
  // tab feeling responsive on open; users can still widen it manually.
  const [selectedRange, setSelectedRange] = useState<string>('1W');
  const [riskTokenType, setRiskTokenType] = useState<string>(() =>
    inferDefaultRiskTokenType(account.name),
  );
  const [selectedMetrics, setSelectedMetrics] = useState<MetricKey[]>([...METRICS]);
  const prevSelectedMetricsRef = useRef<MetricKey[]>(selectedMetrics);

  const isCombined = selectedToken === COMBINED_USD_TOKEN;
  // The combined view is a USD total by definition, so it always fetches in
  // QUOTE regardless of the BASE/QUOTE toggle; single-symbol books honour it.
  const effectiveRiskTokenType = isCombined ? 'QUOTE' : riskTokenType;

  // Every book in play for this selection: all constituents when combined, the
  // one selected book otherwise. The ticker takes this whole set and applies
  // the cash/greek rule itself.
  const activeSymbols = useMemo(
    () => (isCombined ? combinedSymbols : [selectedToken]),
    [isCombined, combinedSymbols, selectedToken],
  );

  // The books that feed a given metric. In the combined view PV sums every
  // book (BTC + cash) while greeks come from the non-stablecoin book only —
  // stablecoin balances are risk-free cash. Single-symbol views fetch just the
  // selected book for every metric.
  const tokensForMetric = useCallback(
    (metric: MetricKey): string[] =>
      isCombined ? contributingSymbolsForMetric(combinedSymbols, metric) : [selectedToken],
    [isCombined, combinedSymbols, selectedToken],
  );

  const pvState = useMetricTimeseries('PV');
  const deltaState = useMetricTimeseries('DELTA');
  const gammaState = useMetricTimeseries('GAMMA');
  const thetaState = useMetricTimeseries('THETA');
  const vegaState = useMetricTimeseries('VEGA');

  const allMetricStates = useMemo<MetricState[]>(
    () => [pvState, deltaState, gammaState, thetaState, vegaState],
    [
      pvState.data,
      pvState.loading,
      pvState.error,
      deltaState.data,
      deltaState.loading,
      deltaState.error,
      gammaState.data,
      gammaState.loading,
      gammaState.error,
      thetaState.data,
      thetaState.loading,
      thetaState.error,
      vegaState.data,
      vegaState.loading,
      vegaState.error,
    ],
  );

  const allMetricStatesRef = useRef(allMetricStates);
  allMetricStatesRef.current = allMetricStates;

  const metricsData = useMemo(() => {
    return selectedMetrics.reduce(
      (acc, metric) => {
        const idx = METRICS.indexOf(metric);
        if (idx !== -1) acc[metric] = allMetricStates[idx].data;
        return acc;
      },
      {} as Record<MetricKey, MetricState['data']>,
    );
  }, [selectedMetrics, allMetricStates]);

  const loadingMetrics = useMemo(() => {
    return selectedMetrics.reduce(
      (acc, metric) => {
        const idx = METRICS.indexOf(metric);
        if (idx !== -1) acc[metric] = allMetricStates[idx].loading;
        return acc;
      },
      {} as Record<MetricKey, boolean>,
    );
  }, [selectedMetrics, allMetricStates]);

  const errorMetrics = useMemo(() => {
    return selectedMetrics.reduce(
      (acc, metric) => {
        const idx = METRICS.indexOf(metric);
        if (idx !== -1) acc[metric] = allMetricStates[idx].error;
        return acc;
      },
      {} as Record<MetricKey, boolean>,
    );
  }, [selectedMetrics, allMetricStates]);

  const refetchAll = useCallback(() => {
    selectedMetrics.forEach((metric) => {
      const idx = METRICS.indexOf(metric);
      if (idx !== -1) {
        allMetricStatesRef.current[idx].refetch({
          account,
          tokens: tokensForMetric(metric),
          range: selectedRange,
          riskTokenType: effectiveRiskTokenType,
        });
      }
    });
  }, [account, tokensForMetric, selectedRange, effectiveRiskTokenType, selectedMetrics]);

  const refetchMetric = useCallback(
    (metric: MetricKey) => {
      const idx = METRICS.indexOf(metric);
      if (idx !== -1) {
        allMetricStatesRef.current[idx].refetch({
          account,
          tokens: tokensForMetric(metric),
          range: selectedRange,
          riskTokenType: effectiveRiskTokenType,
        });
      }
    },
    [account, tokensForMetric, selectedRange, effectiveRiskTokenType],
  );

  useEffect(() => {
    const prev = prevSelectedMetricsRef.current;
    const added = selectedMetrics.filter((m) => !prev.includes(m));
    added.forEach((m) => {
      refetchMetric(m);
    });
    prevSelectedMetricsRef.current = selectedMetrics;
  }, [selectedMetrics]);

  useEffect(() => {
    if (account) refetchAll();
  }, [account, selectedToken, selectedRange, riskTokenType]);

  const fetchHistoricalChunk = useCallback(
    (metric: MetricKey, toTs: number) => {
      const windowSec = windowSecondsFor(selectedRange);
      const frequency = frequencySecondsFor(selectedRange);
      const beginTs = toTs - windowSec;
      const begin = moment.unix(beginTs).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      const end = moment.unix(toTs).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
      const idx = METRICS.indexOf(metric);
      if (idx !== -1) {
        allMetricStatesRef.current[idx].refetch({
          account,
          tokens: tokensForMetric(metric),
          riskTokenType: effectiveRiskTokenType,
          begin,
          end,
          frequency,
          append: true,
        });
      }
    },
    [account, tokensForMetric, selectedRange, effectiveRiskTokenType],
  );

  const { subscribeToWsTicker, resubscribe } = useAccountTicker(
    account,
    activeSymbols,
    effectiveRiskTokenType,
    isCombined,
  );
  const tickListeners = useRef<Set<(t: TickPayload) => void>>(new Set());

  const subscribeToPerformanceTick = useCallback((fn: (t: TickPayload) => void) => {
    tickListeners.current.add(fn);
    return () => {
      tickListeners.current.delete(fn);
    };
  }, []);

  useEffect(() => {
    if (!account) return;
    const off = subscribeToWsTicker((tick) => {
      tickListeners.current.forEach((fn) => fn(tick));
    });
    return off;
  }, [account, subscribeToWsTicker]);

  useSmartInactivityWake(() => {
    resubscribe();
    refetchAll();
  }, INACTIVITY_TIMEOUT);

  return {
    selectedToken,
    setSelectedToken,
    selectedRange,
    setSelectedRange,
    selectedMetrics,
    setSelectedMetrics,
    riskTokenType,
    setRiskTokenType,
    metricsData,
    loadingMetrics,
    errorMetrics,
    refetchAll,
    refetchMetric,
    subscribeToPerformanceTick,
    fetchHistoricalChunk,
  };
};
